import { useState } from 'react';
import { EmptyState } from '../components/States';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { LocationSelect } from '../components/Operation';
import { WAREHOUSE_TABS } from '../nav/navModel';
import { DispatchPanel } from '../ops/DispatchPanel';
import { EntryPanel } from '../ops/EntryPanel';
import { SalePanel } from '../ops/SalePanel';
import { SalesHistory } from '../ops/SalesHistory';
import { useLoad } from '../lib/useAsync';
import { bogotaDay, formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

type Tab = (typeof WAREHOUSE_TABS)[number];

/** Bodega: entrada rápida (y por curva), venta a locales (despacho), venta directa y mercancía en tránsito. */
export function Warehouse() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user, can } = useAuth();
  const warehouses = (user?.locations ?? []).filter((l) => l.type === 'WAREHOUSE');
  const [selected, setSelected] = useState<number | null>(null);
  const locationId = selected ?? warehouses[0]?.id ?? null;
  const [tab, setTab] = useState<Tab>('receive');

  const locations = useLoad(() => api.locations(), []);
  const methods = useLoad(() => api.paymentMethods(), []);
  const stock = useLoad(() => (locationId ? api.inventorySummary(locationId) : Promise.resolve(null)), [locationId]);
  const transit = useLoad(() => (locationId ? api.shipments({ status: 'OPEN', origin_id: locationId }) : Promise.resolve([])), [locationId]);
  const sales = useLoad(() => (locationId && can('sale.create') ? api.sales({ location_id: locationId, channel: 'direct', limit: 30 }) : Promise.resolve([])), [locationId, can('sale.create')]);
  const refresh = () => {
    void stock.reload();
    void transit.reload();
    void sales.reload();
  };

  const units = stock.data?.units ?? 0;
  const value = stock.data?.value_landed ?? 0;
  const today = bogotaDay(new Date());
  const directToday = (sales.data ?? []).filter((s) => s.status === 'ACTIVE' && bogotaDay(s.sold_at) === today).reduce((s, r) => s + r.total, 0);
  const tabs = WAREHOUSE_TABS.filter((k) => (k === 'receive' ? can('inventory.entry') : k === 'dispatch' ? can('shipment.dispatch') : k === 'direct' ? can('sale.create') : true));
  const activeTab = tabs.includes(tab) ? tab : tabs[0];

  return (
    <>
      <LocationSelect id="warehouseLocation" locations={warehouses} value={locationId} onChange={setSelected} />
      <div className="kpis">
        <div className="kpi">
          <div className="lab">{t('warehouse.kpiStock')}</div>
          <div className="val" id="wStock">{formatNumber(units, lang)}</div>
          <div className="sub">{t('warehouse.kpiStockSub')}</div>
        </div>
        {can('cost.view') ? (
          <div className="kpi">
            <div className="lab">{t('warehouse.kpiValue')}</div>
            <div className="val" id="wCapital">{formatMoney(value, lang)}</div>
            <div className="sub">{t('warehouse.kpiValueSub')}</div>
          </div>
        ) : null}
        <div className="kpi">
          <div className="lab">{t('warehouse.kpiPending')}</div>
          <div className="val" id="wPending">{formatNumber(transit.data?.length ?? 0, lang)}</div>
          <div className="sub">{t('warehouse.kpiPendingSub')}</div>
        </div>
        <div className="kpi">
          <div className="lab">{t('warehouse.kpiDirect')}</div>
          <div className="val" id="wDirectSales">{formatMoney(directToday, lang)}</div>
          <div className="sub">{t('warehouse.kpiDirectSub')}</div>
        </div>
      </div>

      <div className="tabs" id="warehouseTabs">
        {tabs.map((k) => (
          <button type="button" key={k} data-tab={k} className={k === activeTab ? 'active' : ''} onClick={() => setTab(k)}>
            {t(`warehouseTabs.${k}`)}
          </button>
        ))}
      </div>

      {activeTab === 'receive' ? <div className="wtab" id="wt-receive"><EntryPanel own={false} locationId={locationId} onDone={refresh} /></div> : null}

      {activeTab === 'dispatch' ? (
        <div className="wtab" id="wt-dispatch">
          <DispatchPanel
            idPrefix="dispatch" originId={locationId} destinations={locations.data ?? []} onDispatched={refresh}
            header={<><h2>{t('warehouse.dispatchHeading')}</h2><p>{t('warehouse.dispatchHelp')}</p></>}
          />
        </div>
      ) : null}

      {activeTab === 'direct' ? (
        <div className="wtab" id="wt-direct">
          <SalePanel
            idPrefix="direct" channel="direct" locationId={locationId} methods={methods.data ?? []} onSold={refresh}
            header={<><h2>{t('warehouse.directHeading')}</h2><p>{t('warehouse.directHelp')}</p></>}
          />
          <div style={{ marginTop: 14 }}>
            <SalesHistory sales={sales.data ?? []} onChanged={refresh} methods={methods.data ?? []} />
          </div>
        </div>
      ) : null}

      {activeTab === 'transit' ? (
        <div className="wtab" id="wt-transit">
          <Panel title={t('warehouse.transitTitle')} description={t('warehouse.transitHelp')}>
            <div id="transitList" className="alerts">
              {(transit.data ?? []).map((s) => (
                <div className="alert" key={s.id} data-shipment={s.doc_no}>
                  <div>
                    <b>{s.destination_name}</b>
                    <small>
                      {[s.doc_no, t('ops.unitsCount', { n: formatNumber(s.units, lang) }), formatMoney(s.total ?? 0, lang), formatDate(s.dispatched_at, lang), t('warehouse.hours', { n: formatNumber(s.hours_in_transit, lang) })].join(' · ')}
                    </small>
                  </div>
                  <Badge tone="amber">{t('shipmentStatus.DISPATCHED')}</Badge>
                </div>
              ))}
              {transit.data && transit.data.length === 0 ? <EmptyState icon="truck" title={t('warehouse.transitEmpty')} hint={t('warehouse.transitEmptyHint')} /> : null}
            </div>
          </Panel>
        </div>
      ) : null}
    </>
  );
}
